/** Phase 19 */
import { getAggregateDiscrepancyManagement } from '../infrastructure/storage/domainAggregateStore';
import { getEntityDiscrepancyManagement } from '../infrastructure/storage/entityAggregateStore';
import { kvGet, kvGetRaw, kvSet } from '../infrastructure/storage/kvAccess';
import { createRootDocumentEntityReadSource } from './rootDocumentEntityReadSourceFactory';
import {
  hasDiscrepancyManagementContent,
  mergeDiscrepancySources,
  normalizeDiscrepancyManagement,
  expandDiscrepancyManagementForDisplay
} from './resolveDiscrepancyForEcarts';
import {
  listBusinessMergedPayloadKeyCandidates,
  normalizeBusinessStorageId,
  readBestBusinessMergedPayload,
  reconstructBusinessMergedPayloadFromElkheirV1
} from './sharedCore';
import { unwrapEnvelopeRawStringToInnerJsonString } from './localStorageCompression';

export { hasDiscrepancyManagementContent } from './resolveDiscrepancyForEcarts';

const EMPTY_DISCREPANCY = {
  selfDeviations: [],
  handoverDeviations: [],
  workerTolerances: {},
  pendingReviews: [],
  auditTrail: [],
  notifications: [],
  toleranceSystemEnabled: false
};

const source = createRootDocumentEntityReadSource({
  field: 'discrepancyManagement',
  getEntity: getEntityDiscrepancyManagement,
  getAggregate: getAggregateDiscrepancyManagement,
  emptyValue: EMPTY_DISCREPANCY,
  hasContent: hasDiscrepancyManagementContent
});

/** @type {Map<string, object>} */
const mergedReadCache = new Map();

const DISCREPANCY_KV_PREFIX = 'Elkheir:v1:discrepancy:';

export function discrepancyKvStorageKey(businessId) {
  const bid = normalizeBusinessStorageId(businessId);
  return bid ? `${DISCREPANCY_KV_PREFIX}${bid}` : null;
}

export function readDiscrepancyKvBackup(businessId) {
  const key = discrepancyKvStorageKey(businessId);
  if (!key) return null;
  const raw = kvGet(key);
  if (!raw || typeof raw !== 'object') return null;
  const normalized = normalizeDiscrepancyManagement(raw);
  return hasDiscrepancyManagementContent(normalized) ? normalized : null;
}

function writeDiscrepancyKvBackup(businessId, doc) {
  const key = discrepancyKvStorageKey(businessId);
  if (!key) return;
  kvSet(key, normalizeDiscrepancyManagement(doc));
}

function parseKvBusinessPayload(key, rawStored) {
  if (rawStored == null || rawStored === '') return null;
  try {
    const unwrapped = unwrapEnvelopeRawStringToInnerJsonString(key, rawStored);
    if (unwrapped == null || unwrapped === '') return null;
    return typeof unwrapped === 'string' ? JSON.parse(unwrapped) : unwrapped;
  } catch {
    return null;
  }
}

/**
 * Scan all legacy KV business blobs — même en mode NO_KV_BLOB (blob canonique supprimé).
 */
export function readLegacyDiscrepancyFromKvCandidates(businessId) {
  if (!businessId) return null;

  const candidates = [];
  const consider = (parsed) => {
    const dm = parsed?.discrepancyManagement;
    if (hasDiscrepancyManagementContent(dm)) {
      candidates.push(normalizeDiscrepancyManagement(dm));
    }
  };

  const best = readBestBusinessMergedPayload(businessId);
  consider(best.parsed);

  for (const key of listBusinessMergedPayloadKeyCandidates(businessId)) {
    if (key === best.key) continue;
    const rawStored = kvGetRaw(key);
    consider(parseKvBusinessPayload(key, rawStored));
  }

  const elkParsed = reconstructBusinessMergedPayloadFromElkheirV1(businessId);
  consider(elkParsed);

  if (candidates.length === 0) return null;
  return mergeDiscrepancySources(...candidates);
}

/** Écrit directement dans entity_discrepancy_management (fiable en mode NO_KV_BLOB). */
export async function writeDiscrepancyManagementDocument(businessId, doc) {
  if (!businessId || !doc) return;

  const normalized = normalizeDiscrepancyManagement(doc);
  writeDiscrepancyKvBackup(businessId, normalized);

  try {
    const { isEntityStoreEnabled, ensureEntityStoreTablesReady, syncEntityStoresFromPayload } =
      await import('../infrastructure/storage/entityAggregateStore');
    if (!isEntityStoreEnabled()) {
      cacheMergedRead(businessId, normalized);
      return;
    }

    const db = await ensureEntityStoreTablesReady();
    const payload = {
      discrepancyManagement: normalized,
      payloadUpdatedAt: new Date().toISOString()
    };
    await syncEntityStoresFromPayload(db, businessId, payload, ['discrepancyManagement']);

    const { isDomainAggregateIndexEnabled, syncAggregateIndexFromPayload } = await import(
      '../infrastructure/storage/domainAggregateStore'
    );
    if (isDomainAggregateIndexEnabled()) {
      await syncAggregateIndexFromPayload(businessId, payload, ['discrepancyManagement']);
    }

    cacheMergedRead(businessId, normalized);
    source.clearReadCache(businessId);
  } catch (err) {
    console.error('[discrepancyRead] write document failed:', err?.message || err);
    throw err;
  }
}

async function persistRecoveredDiscrepancyManagement(businessId, doc) {
  if (!hasDiscrepancyManagementContent(doc)) return;
  await writeDiscrepancyManagementDocument(businessId, doc);
}

async function recoverDiscrepancyManagementIfEmpty(businessId, current) {
  if (hasDiscrepancyManagementContent(current)) return current;

  const legacy = readLegacyDiscrepancyFromKvCandidates(businessId);
  if (!hasDiscrepancyManagementContent(legacy)) return current;

  const merged = mergeDiscrepancySources(current, legacy);
  await persistRecoveredDiscrepancyManagement(businessId, merged);
  return merged;
}

async function readAllDiscrepancySources(businessId) {
  const [fromEntity, fromAggregate, fromBlob, fromLegacy, fromKvBackup] = await Promise.all([
    getEntityDiscrepancyManagement(businessId).catch(() => null),
    getAggregateDiscrepancyManagement(businessId).catch(() => null),
    Promise.resolve(source.readFromMergedBlob(businessId)),
    Promise.resolve(readLegacyDiscrepancyFromKvCandidates(businessId)),
    Promise.resolve(readDiscrepancyKvBackup(businessId))
  ]);

  const merged = mergeDiscrepancySources(
    fromEntity,
    fromAggregate,
    fromBlob,
    fromLegacy,
    fromKvBackup
  );
  const expanded = expandDiscrepancyManagementForDisplay(merged);
  if (
    hasDiscrepancyManagementContent(expanded) &&
    (expanded.selfDeviations?.length || 0) + (expanded.handoverDeviations?.length || 0) >
      (merged.selfDeviations?.length || 0) + (merged.handoverDeviations?.length || 0)
  ) {
    await writeDiscrepancyManagementDocument(businessId, expanded);
    return expanded;
  }
  return recoverDiscrepancyManagementIfEmpty(businessId, merged);
}

function cacheMergedRead(businessId, doc) {
  if (!businessId) return;
  mergedReadCache.set(String(businessId), doc);
}

export async function readDiscrepancyManagementFromBestSource(businessId) {
  if (!businessId) return { ...EMPTY_DISCREPANCY };

  const key = String(businessId);
  const cached = mergedReadCache.get(key);
  if (cached && hasDiscrepancyManagementContent(cached)) return cached;
  if (cached && !hasDiscrepancyManagementContent(cached)) {
    mergedReadCache.delete(key);
  }

  const doc = await readAllDiscrepancySources(businessId);
  cacheMergedRead(businessId, doc);
  return doc;
}

export function readDiscrepancyManagementFromMergedBlob(businessId) {
  return source.readFromMergedBlob(businessId);
}

export async function refreshDiscrepancyManagementReadCache(businessId, preloaded) {
  if (preloaded !== undefined) {
    cacheMergedRead(businessId, preloaded);
    return preloaded;
  }
  source.clearReadCache(businessId);
  if (businessId) {
    mergedReadCache.delete(String(businessId));
  }
  const doc = await readAllDiscrepancySources(businessId);
  cacheMergedRead(businessId, doc);
  return doc;
}

export async function ensureDiscrepancyManagementEntityHydrated(businessId) {
  if (!businessId) return { skipped: true, reason: 'no_business' };

  const doc = await refreshDiscrepancyManagementReadCache(businessId);
  const count =
    (doc.selfDeviations?.length || 0) + (doc.handoverDeviations?.length || 0);

  return {
    ok: true,
    businessId: String(businessId),
    ecartCount: count,
    recovered: count > 0
  };
}

export function getCachedDiscrepancyManagementRead(businessId) {
  if (!businessId) return null;
  const merged = mergedReadCache.get(String(businessId));
  if (merged && hasDiscrepancyManagementContent(merged)) return merged;
  if (merged && !hasDiscrepancyManagementContent(merged)) {
    mergedReadCache.delete(String(businessId));
  }
  const cached = source.getCachedRead(businessId);
  if (cached && hasDiscrepancyManagementContent(cached)) return cached;
  return null;
}

export function clearDiscrepancyManagementReadCache(businessId) {
  source.clearReadCache(businessId);
  if (businessId) {
    mergedReadCache.delete(String(businessId));
  } else {
    mergedReadCache.clear();
  }
}

export function readDiscrepancyManagementSyncForBusiness(businessId) {
  if (!businessId) return { ...EMPTY_DISCREPANCY };
  const cached = getCachedDiscrepancyManagementRead(businessId);
  if (cached) return cached;

  const legacy = readLegacyDiscrepancyFromKvCandidates(businessId);
  if (hasDiscrepancyManagementContent(legacy)) return legacy;

  const kvBackup = readDiscrepancyKvBackup(businessId);
  if (hasDiscrepancyManagementContent(kvBackup)) return kvBackup;

  return source.readSyncForBusiness(businessId);
}